import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
import api from "../../api/axiosInstance"
import AppBarComponent from "../../components/layout/AppbarComponent";
import { useConfirm } from "../../utils/useConfirm";
import { toast,ToastContainer } from "react-toastify";

export default function SummaryCost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const {confirm} = useConfirm();

  const [software, setSoftware] = useState({});
  const [loading, setLoading] = useState(true);

  // Load software (including ratios + salary + total manhours)
  useEffect(() => {
    const loadSoftware = async () => {
      try {
        const res = await api.get(`api/software/getsoftware/${id}`);
        setSoftware(res.data);
      } catch (err) {
        console.error("Error loading summary:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSoftware();
  }, [id, API_URL]);

  if (loading) return <Typography>กำลังโหลดข้อมูล...</Typography>;

  // ---------------------------------------------------------------------
  // Extract all values from database
  // ---------------------------------------------------------------------
  const baseLineMH = (software.dev_manhours * 100) / software.dev_ratio;
  //   const totalMH = Number(software.dev_manhours || 0);

  const sdlcRatio = {
    การวางแผน: software.plan_ratio || 0,
    การวิเคราะห์ความต้องการ: software.req_ratio || 0,
    การออกแบบซอฟต์แวร์: software.design_ratio || 0,
    การพัฒนาซอฟต์แวร์: software.dev_ratio || 0,
    การทดสอบซอฟต์แวร์: software.test_ratio || 0,
    การส่งมอบ: software.deliver_ratio || 0,
    // ความเสี่ยง: software.risk_ratio || 0,
    // เทคโนโลยีที่คิดค้นขึ้นใหม่: software.tech_ratio || 0,
    // ปัจจัยอื่นๆ: software.other_ratio || 0,
  };

  const supportRatio = {
    ความเสี่ยง: software.risk_ratio || 0,
    เทคโนโลยีที่คิดค้นขึ้นใหม่: software.tech_ratio || 0,
    ปัจจัยอื่นๆ: software.other_ratio || 0,
  };

  const roleSalary = {
    ผู้บริหารโครงการ: software.pm_salary || 0,
    นักวิเคราะห์และออกแบบระบบงาน: software.sa_salary || 0,
    นักพัฒนาซอฟต์แวร์: software.dev_salary || 0,
    นักทดสอบซอฟต์แวร์: software.tester_salary || 0,
    ฝ่ายปฏิบัติการสนับสนุน: software.workteam_salary || 0,
    ผู้เกี่ยวข้อง: software.support_salary || 0,
  };

  const sdlcProcess = {
    การวางแผน: "ผู้บริหารโครงการ",
    การวิเคราะห์ความต้องการ: "นักวิเคราะห์และออกแบบระบบงาน",
    การออกแบบซอฟต์แวร์: "นักวิเคราะห์และออกแบบระบบงาน",
    การพัฒนาซอฟต์แวร์: "นักพัฒนาซอฟต์แวร์",
    การทดสอบซอฟต์แวร์: "นักทดสอบซอฟต์แวร์",
    การส่งมอบ: "ฝ่ายปฏิบัติการสนับสนุน",
  };

  const supportProcess = {
    ความเสี่ยง: "ผู้เกี่ยวข้อง",
    เทคโนโลยีที่คิดค้นขึ้นใหม่: "ผู้เกี่ยวข้อง",
    ปัจจัยอื่นๆ: "ผู้เกี่ยวข้อง",
  };

  const manmonth = (mh) => mh / 152;

  // Calculated rows
  const sdlcRows = Object.keys(sdlcRatio).map((process) => {
    const percent = sdlcRatio[process];
    const mh = (baseLineMH * percent) / 100;
    const mm = manmonth(mh);
    const role = sdlcProcess[process];
    const salary = roleSalary[role];
    const cost = mm * salary;

    return {
      process,
      percent,
      mh,
      mm,
      role,
      salary,
      cost,
    };
  });

  const supportRows = Object.keys(supportRatio).map((process) => {
    const percent = supportRatio[process];
    const mh = (baseLineMH * percent) / 100;
    const mm = manmonth(mh);
    const role = supportProcess[process];
    const salary = roleSalary[role];
    const cost = mm * salary;

    return {
      process,
      percent,
      mh,
      mm,
      role,
      salary,
      cost,
    };
  });

  const totalSdlc = sdlcRows.reduce((sum, r) => sum + r.cost, 0);
  const totalSupport = supportRows.reduce((sum, r) => sum + r.cost, 0);
  const totalCost = totalSdlc + totalSupport;

  /* SUMMARIZE TOTAL EFFORT */
  const totalPercent =
    sdlcRows.reduce((sum, r) => sum + Number(r.percent || 0), 0) +
    supportRows.reduce((sum, r) => sum + Number(r.percent || 0), 0);

  const totalMH =
    sdlcRows.reduce((sum, r) => sum + Number(r.mh || 0), 0) +
    supportRows.reduce((sum, r) => sum + Number(r.mh || 0), 0);

  const handleSave = async () => {
    try {
      await api.put(`api/software/editcost`, {
        software_id: id,
        cost: Number(totalCost).toFixed(2),
      });

      toast.success("บันทึก Cost สำเร็จ");
      setSoftware((prev) => ({
        ...prev,
        cost: Number(totalCost).toFixed(2),
      }));
    } catch (err) {
      console.error(err);
      toast.error("บันทึกไม่สำเร็จ");
    }
  };

  const handleBack = async () => {
    const hasChanges = Number(software.cost || 0) !== Number(totalCost.toFixed(2));
    if (hasChanges) {
      const ok = await confirm(
        "คุณยังไม่ได้บันทึกการเปลี่ยนแปลง ต้องการดำเนินการต่อหรือไม่?"
      );
      if (!ok) return;
    }
    navigate(`/manhours/${id}`);
  };

  const handleNext = async () => {
    const hasChanges = Number(software.cost || 0) !== Number(totalCost.toFixed(2));
    if (hasChanges) {
      const ok = await confirm(
        "คุณยังไม่ได้บันทึกการเปลี่ยนแปลง ต้องการดำเนินการต่อหรือไม่?"
      );
      if (!ok) return;
    }
    navigate(`/summaryduration/${id}`);
  };
  

  // ---------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------
  return (
    <>
      <CssBaseline />
      <AppBarComponent />
      <ToastContainer position="top-center" autoClose={2000} />

      <Box sx={{ p: 2, background: "#DFF6F0", minHeight: "100vh" }}>
        <Paper sx={{ width: "95%", m: "0 auto", p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            สรุปต้นทุนโครงการ :
          </Typography>
          <Typography variant="h6" mb={2} sx={{ fontWeight: "bold" }}>
            {id}
            {"_"}
            {software.software_name ? software.software_name : ""}
          </Typography>

          <Typography variant="h6" fontWeight="bold">
            Man-Hour รวมจากการประเมินฟังก์ชัน: {software.dev_manhours} ชั่วโมง
          </Typography>

          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>กระบวนการ</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>อัตราส่วน</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>ค่าความพยายาม (Man-Hours)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>ค่าความพยายาม (Man-Month)</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    บทบาทหน้าที่หลัก
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    อัตราค่าจ้างต่อเดือน (บาท)
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>รวมค่าจ้าง (บาท)</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sdlcRows.map((r, index) => (
                  <TableRow key={index}>
                    <TableCell>{r.process}</TableCell>
                    <TableCell>{r.percent}%</TableCell>
                    <TableCell>{r.mh.toFixed(2)}</TableCell>
                    <TableCell>{r.mm.toFixed(2)}</TableCell>
                    <TableCell>{r.role}</TableCell>
                    <TableCell>{r.salary}</TableCell>
                    <TableCell>{r.cost.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>รวม</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {sdlcRows
                      .reduce((sum, r) => sum + Number(r.percent || 0), 0)
                      .toFixed(2)}
                    %
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {sdlcRows
                      .reduce((sum, r) => sum + Number(r.mh || 0), 0)
                      .toFixed(2)}
                  </TableCell>
                </TableRow>
                {supportRows.map((r, index) => (
                  <TableRow key={index}>
                    <TableCell>{r.process}</TableCell>
                    <TableCell>{r.percent}%</TableCell>
                    <TableCell>{r.mh.toFixed(2)}</TableCell>
                    <TableCell>{r.mm.toFixed(2)}</TableCell>
                    <TableCell>{r.role}</TableCell>
                    <TableCell>{r.salary}</TableCell>
                    <TableCell>{r.cost.toFixed(2)}</TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    รวมค่าความพยายาม
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {totalPercent.toFixed(2)} %
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {totalMH.toFixed(2)}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    รวมค่าจ้างในการพัฒนาซอฟต์แวร์
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {totalCost.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              sx={{ fontWeight: "bold" }}
            >
              บันทึก
            </Button>

            <ButtonGroup>
              <Button
                variant="contained"
                color="inherit"
                onClick={handleBack}
                sx={{ fontWeight: "bold" }}
              >
                ย้อนกลับ
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                sx={{ fontWeight: "bold" }}
              >
                ถัดไป
              </Button>
            </ButtonGroup>
          </Box>
        </Paper>
      </Box>
    </>
  );
}
